import React, { useState, useEffect } from 'react';
import { Input, Modal, Table, Button, Form, Input as AntdInput, Drawer, Checkbox, Select, Row, Col } from 'antd';
import { PlusOutlined, FilterOutlined, EditOutlined, InfoCircleOutlined, QrcodeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './AssetManagement.css';
const { Search: AntSearch } = Input;
const { Option } = Select;

function AssetManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddModalVisible, setIsAddModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isAssignModalVisible, setIsAssignModalVisible] = useState(false);
  const [isFilterDrawerVisible, setIsFilterDrawerVisible] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [assignRecord, setAssignRecord] = useState(null);
  const [cardData, setCardData] = useState([]);
  const [tableFilters, setTableFilters] = useState({});
  const [visibleColumns, setVisibleColumns] = useState({
    assetId: true,
    assetCode: true,
    name: true,
    assignTo: true,
    handoverDate: true,
    received: true,
    invoiceNo: false,
    price: false,
    brand: true,
    whoAssigned: true,
  });

  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const [assignForm] = Form.useForm();
  const navigate = useNavigate();

  const users = [
    'John Doe', 'Jane Smith', 'Mike Johnson', 'Sarah Williams', 'Current User',
    'Alice Johnson', 'Michael Green', 'Laura Wilson', 'Chris Evans', 'Emma Watson',
    'Ryan Reynolds', 'Sophia Turner', 'Nathan Drake', 'Diana Prince', 'Clark Kent',
    'Peter Parker', 'Bruce Banner', 'Steve Rogers', 'Wanda Maximoff', 'Tony Stark',
    'Barry Allen', 'Derek Shepherd', 'Monica Geller', 'Ross Geller'
  ];

  const [isInfoModalVisible, setIsInfoModalVisible] = useState(false);
  const [infoRecord, setInfoRecord] = useState(null);
  const [isUserInfoModalVisible, setIsUserInfoModalVisible] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [isAssignerInfoModalVisible, setIsAssignerInfoModalVisible] = useState(false);
  const [assignerInfo, setAssignerInfo] = useState(null);

  const handleInfo = (record) => {
    setInfoRecord(record);
    setIsInfoModalVisible(true);
  };

  const handleInfoCancel = () => {
    setIsInfoModalVisible(false);
    setInfoRecord(null);
  };

  const handleUserInfo = (user) => {
    const userAssets = cardData
      .filter((item) => item.assignTo === user)
      .map((item) => ({
        assetName: item.name,
        assetCode: item.assetCode,
        action: 'Assigned',
        date: item.handoverDate,
        location: 'Office',
        whoAssigned: item.whoAssigned,
        received: item.received === 'Yes' ? 'Received' : 'Not Received',
        appliedForFix: item.assetHistory.some((h) => h.action === 'Applied for Fix') ? 'Yes' : 'No',
      }));

    setUserInfo({ user, assets: userAssets });
    setIsUserInfoModalVisible(true);
  };

  const handleUserInfoCancel = () => {
    setIsUserInfoModalVisible(false);
    setUserInfo(null);
  };

  const handleAssignerInfo = (assigner) => {
    const assignedAssets = cardData
      .filter((item) => item.whoAssigned === assigner)
      .map((item) => ({
        assetName: item.name,
        assetCode: item.assetCode,
        assignedTo: item.assignTo,
        date: item.handoverDate,
        location: 'Office',
      }));

    setAssignerInfo({ assigner, assets: assignedAssets });
    setIsAssignerInfoModalVisible(true);
  };

  const handleAssignerInfoCancel = () => {
    setIsAssignerInfoModalVisible(false);
    setAssignerInfo(null);
  };

  const handleQRView = (record) => {
    navigate(`/qr/?asset_id=${record.assetId}`);
  };

  useEffect(() => {
    fetch('/cardData.json')
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const enrichedData = data.map((item) => ({
          ...item,
          assetHistory: [
            { action: 'Purchased', by: 'Admin', to: null, date: '2024-11-01', location: 'Warehouse A' },
            { action: 'Assigned', by: item.whoAssigned, to: item.assignTo, date: item.handoverDate, location: 'Office' },
            ...(item.received === 'Yes' ? [{ action: 'Received', by: item.assignTo, to: null, date: item.handoverDate, location: 'Office' }] : []),
          ],
        }));
        setCardData(enrichedData);
      })
      .catch((error) => console.error('Error fetching data:', error));
  }, []);

  const getColumnFilters = (dataIndex) => {
    const uniqueValues = [...new Set(cardData.map((item) => item[dataIndex]))];
    return uniqueValues.map((value) => ({ text: value, value }));
  };

  const filteredData = cardData.filter((record) => {
    const matchesSearch = Object.values(record).some((value) =>
      value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesFilters = Object.keys(tableFilters).every((key) => {
      const filterValues = tableFilters[key];
      if (!filterValues || filterValues.length === 0) return true;
      return filterValues.includes(record[key]);
    });

    return matchesSearch && matchesFilters;
  });

  const handleEdit = (record) => {
    setEditRecord(record);
    form.setFieldsValue(record);
    setIsEditModalVisible(true);
  };

  const handleEditCancel = () => setIsEditModalVisible(false);

  const handleEditSave = (values) => {
    setCardData(cardData.map((item) => (item.assetId === editRecord.assetId ? { ...editRecord, ...values } : item)));
    setIsEditModalVisible(false);
  };

  const showAddModal = () => setIsAddModalVisible(true);
  const handleAddCancel = () => setIsAddModalVisible(false);

  const handleAddAsset = (values) => {
    const newAsset = {
      ...values,
      assetId: `A${String(cardData.length + 1).padStart(3, '0')}`,
      assetHistory: [
        { action: 'Added', by: 'Current User', to: values.assignTo || null, date: new Date().toISOString().split('T')[0], location: 'Unknown' },
      ],
    };
    setCardData([...cardData, newAsset]);
    setIsAddModalVisible(false);
    addForm.resetFields();
  };

  const handleAssign = (record) => {
    setAssignRecord(record);
    assignForm.setFieldsValue({
      assignTo: record.assignTo,
      whoAssigned: 'Current User',
    });
    setIsAssignModalVisible(true);
  };

  const handleAssignCancel = () => {
    setIsAssignModalVisible(false);
    assignForm.resetFields();
  };

  const handleAssignSave = (values) => {
    const updatedCardData = cardData.map((item) =>
      item.assetId === assignRecord.assetId
        ? {
            ...item,
            assignTo: values.assignTo,
            whoAssigned: values.whoAssigned,
            assetHistory: [
              ...(item.assetHistory || []),
              { action: 'Assigned', by: values.whoAssigned, to: values.assignTo, date: new Date().toISOString().split('T')[0], location: 'Office' },
            ],
          }
        : item
    );
    setCardData(updatedCardData);
    setIsAssignModalVisible(false);
    assignForm.resetFields();
  };

  const handleFilterDrawer = (open) => setIsFilterDrawerVisible(open);

  const handleColumnChange = (e) => {
    const { name, checked } = e.target;
    setVisibleColumns((prev) => ({ ...prev, [name]: checked }));
  };

  const columns = [
    visibleColumns.assetId && {
      title: 'Asset ID',
      dataIndex: 'assetId',
      key: 'assetId',
      filters: getColumnFilters('assetId'),
      onFilter: (value, record) => record.assetId === value,
      sorter: (a, b) => a.assetId.localeCompare(b.assetId),
      render: (text, record) => (
        <span>
          <EditOutlined style={{ marginRight: 8, cursor: 'pointer' }} onClick={() => handleEdit(record)} />
          {text}
          <InfoCircleOutlined style={{ marginLeft: 8, cursor: 'pointer' }} onClick={() => handleInfo(record)} />
        </span>
      ),
    },
    visibleColumns.assetCode && {
      title: 'Asset Code',
      dataIndex: 'assetCode',
      key: 'assetCode',
      filters: getColumnFilters('assetCode'),
      onFilter: (value, record) => record.assetCode === value,
      sorter: (a, b) => a.assetCode.localeCompare(b.assetCode),
    },
    visibleColumns.name && {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      filters: getColumnFilters('name'),
      onFilter: (value, record) => record.name === value,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    visibleColumns.assignTo && {
      title: 'Assigned To',
      dataIndex: 'assignTo',
      key: 'assignTo',
      filters: getColumnFilters('assignTo'),
      onFilter: (value, record) => record.assignTo === value,
      sorter: (a, b) => a.assignTo.localeCompare(b.assignTo),
      render: (text, record) => (
        <span>
          {text}
          <InfoCircleOutlined
            style={{ marginLeft: 8, cursor: 'pointer' }}
            onClick={() => handleUserInfo(text)}
          />
        </span>
      ),
    },
    visibleColumns.handoverDate && {
      title: 'Handover Date',
      dataIndex: 'handoverDate',
      key: 'handoverDate',
      filters: getColumnFilters('handoverDate'),
      onFilter: (value, record) => record.handoverDate === value,
      sorter: (a, b) => new Date(a.handoverDate) - new Date(b.handoverDate),
    },
    visibleColumns.received && {
      title: 'Received',
      dataIndex: 'received',
      key: 'received',
      filters: getColumnFilters('received'),
      onFilter: (value, record) => record.received === value,
      sorter: (a, b) => a.received.localeCompare(b.received),
    },
    visibleColumns.invoiceNo && {
      title: 'Invoice No',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      filters: getColumnFilters('invoiceNo'),
      onFilter: (value, record) => record.invoiceNo === value,
      sorter: (a, b) => a.invoiceNo.localeCompare(b.invoiceNo),
    },
    visibleColumns.price && {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      filters: getColumnFilters('price'),
      onFilter: (value, record) => record.price === value,
      sorter: (a, b) => parseFloat(a.price.replace('$', '').replace(',', '')) - parseFloat(b.price.replace('$', '').replace(',', '')),
    },
    visibleColumns.brand && {
      title: 'Brand',
      dataIndex: 'brand',
      key: 'brand',
      filters: getColumnFilters('brand'),
      onFilter: (value, record) => record.brand === value,
      sorter: (a, b) => a.brand.localeCompare(b.brand),
    },
    visibleColumns.whoAssigned && {
      title: 'Who Assigned',
      dataIndex: 'whoAssigned',
      key: 'whoAssigned',
      filters: getColumnFilters('whoAssigned'),
      onFilter: (value, record) => record.whoAssigned === value,
      sorter: (a, b) => a.whoAssigned.localeCompare(b.whoAssigned),
      render: (text, record) => (
        <span>
          {text}
          <InfoCircleOutlined
            style={{ marginLeft: 8, cursor: 'pointer' }}
            onClick={() => handleAssignerInfo(text)}
          />
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (text, record) => (
        <div>
          <Button type="primary" onClick={() => handleAssign(record)} style={{ marginRight: 8 }}>
            Assign
          </Button>
          <Button 
            icon={<QrcodeOutlined />}
            onClick={() => handleQRView(record)}
          />
        </div>
      ),
    },
  ].filter(Boolean);

  const handleTableChange = (pagination, filters, sorter) => {
    setTableFilters(filters);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8 m-4 md:m-6 lg:m-8" style={{ margin: '1rem 1rem' }}>
      <div className="flex justify-between items-center mb-4" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h1 className="text-2xl">Asset Management</h1>
        <div>
          <Button icon={<FilterOutlined />} onClick={() => handleFilterDrawer(true)} style={{ marginRight: '8px' }}>
            Filter
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={showAddModal}>
            Add Asset
          </Button>
        </div>
      </div>

      <AntSearch placeholder="Search assets" size="large" onChange={(e) => setSearchTerm(e.target.value)} />
      <div className="flex justify-between items-center mt-4">
        <span>{`Showing ${filteredData.length} ${filteredData.length === 1 ? 'asset' : 'assets'}`}</span>
      </div>

      <Table
        className="mt-6"
        columns={columns}
        dataSource={filteredData}
        rowKey="assetId"
        bordered
        pagination={{ pageSize: 5 }}
        onChange={handleTableChange}
      />

      <Modal title="Add Asset" visible={isAddModalVisible} onCancel={handleAddCancel} onOk={() => addForm.submit()}>
        <Form form={addForm} layout="vertical" onFinish={handleAddAsset}>
          <Form.Item label="Asset Code" name="assetCode" rules={[{ required: true, message: 'Please input the asset code!' }]}>
            <AntdInput />
          </Form.Item>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please input the name!' }]}>
            <AntdInput />
          </Form.Item>
          <Form.Item label="Assigned To" name="assignTo">
            <AntdInput />
          </Form.Item>
          <Form.Item label="Handover Date" name="handoverDate">
            <AntdInput />
          </Form.Item>
          <Form.Item label="Received" name="received">
            <AntdInput />
          </Form.Item>
          <Form.Item label="Invoice No" name="invoiceNo">
            <AntdInput />
          </Form.Item>
          <Form.Item label="Price" name="price">
            <AntdInput />
          </Form.Item>
          <Form.Item label="Brand" name="brand">
            <AntdInput />
          </Form.Item>
          <Form.Item label="Who Assigned" name="whoAssigned">
            <AntdInput />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Edit Asset"
        visible={isEditModalVisible}
        onCancel={handleEditCancel}
        onOk={() => form.submit()}
        width={800}
      >
        <Form form={form} layout="vertical" onFinish={handleEditSave}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Asset Code" name="assetCode" rules={[{ required: true, message: 'Please input the asset code!' }]}>
                <AntdInput />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Please input the name!' }]}>
                <AntdInput />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Assigned To" name="assignTo">
                <AntdInput />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Handover Date" name="handoverDate">
                <AntdInput />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Received" name="received">
                <AntdInput />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Invoice No" name="invoiceNo">
                <AntdInput />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Price" name="price">
                <AntdInput />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Brand" name="brand">
                <AntdInput />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Who Assigned" name="whoAssigned">
                <AntdInput />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Assign Asset"
        visible={isAssignModalVisible}
        onCancel={handleAssignCancel}
        onOk={() => assignForm.submit()}
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignSave}>
          <Form.Item label="Assign To" name="assignTo" rules={[{ required: true, message: 'Please select who to assign to!' }]}>
            <Select placeholder="Select a user">
              {users.map((user) => (
                <Option key={user} value={user}>{user}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item label="Assigned By" name="whoAssigned" initialValue="Current User">
            <Select placeholder="Select who is assigning">
              {users.map((user) => (
                <Option key={user} value={user}>{user}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Asset Lifecycle"
        visible={isInfoModalVisible}
        onCancel={handleInfoCancel}
        footer={null}
        width={800}
      >
        {infoRecord ? (
          <>
            <div style={{ marginBottom: 16, color: 'grey' }}>
              <strong>Asset Code:</strong> {infoRecord.assetCode} <br />
              <strong>Asset Name:</strong> {infoRecord.name}
            </div>
            <Table
              dataSource={infoRecord.assetHistory}
              pagination={false}
              bordered
              size="middle"
              columns={[
                { title: 'Action', dataIndex: 'action', key: 'action' },
                { title: 'By', dataIndex: 'by', key: 'by' },
                { title: 'To / Assigned To', dataIndex: 'to', key: 'to', render: (text) => text || '—' },
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Location', dataIndex: 'location', key: 'location' },
              ]}
            />
          </>
        ) : (
          <p>Loading...</p>
        )}
      </Modal>

      <Modal
        title="User Asset History"
        visible={isUserInfoModalVisible}
        onCancel={handleUserInfoCancel}
        footer={null}
        width={800}
      >
        {userInfo ? (
          <>
            <div style={{ marginBottom: 16, color: 'grey' }}>
              <strong>User:</strong> {userInfo.user}
            </div>
            <Table
              dataSource={userInfo.assets}
              pagination={false}
              bordered
              size="middle"
              columns={[
                { title: 'Asset Name', dataIndex: 'assetName', key: 'assetName' },
                { title: 'Asset Code', dataIndex: 'assetCode', key: 'assetCode' },
                { title: 'Action', dataIndex: 'action', key: 'action' },
                { title: 'Assigned By', dataIndex: 'whoAssigned', key: 'whoAssigned' },
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Location', dataIndex: 'location', key: 'location' },
                { title: 'Received', dataIndex: 'received', key: 'received' },
                { title: 'Applied for Fix', dataIndex: 'appliedForFix', key: 'appliedForFix' },
              ]}
            />
          </>
        ) : (
          <p>Loading...</p>
        )}
      </Modal>

      <Modal
        title="Assigner Asset History"
        visible={isAssignerInfoModalVisible}
        onCancel={handleAssignerInfoCancel}
        footer={null}
        width={800}
      >
        {assignerInfo ? (
          <>
            <div style={{ marginBottom: 16, color: 'grey' }}>
              <strong>Assigner:</strong> {assignerInfo.assigner} <br />
              <strong>Total Assets Assigned:</strong> {assignerInfo.assets.length}
            </div>
            <Table
              dataSource={assignerInfo.assets}
              pagination={false}
              bordered
              size="middle"
              columns={[
                { title: 'Asset Name', dataIndex: 'assetName', key: 'assetName' },
                { title: 'Asset Code', dataIndex: 'assetCode', key: 'assetCode' },
                { title: 'Assigned To', dataIndex: 'assignedTo', key: 'assignedTo' },
                { title: 'Date', dataIndex: 'date', key: 'date' },
                { title: 'Location', dataIndex: 'location', key: 'location' },
              ]}
            />
          </>
        ) : (
          <p>Loading...</p>
        )}
      </Modal>

      <Drawer
        title="Filter Columns"
        placement="right"
        onClose={() => handleFilterDrawer(false)}
        visible={isFilterDrawerVisible}
      >
        {Object.keys(visibleColumns).map((col) => (
          <div key={col}>
            <Checkbox name={col} checked={visibleColumns[col]} onChange={handleColumnChange}>
              {col}
            </Checkbox>
          </div>
        ))}
      </Drawer>
    </div>
  );
}

export default AssetManagement;